import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import { AppModal, ModalType } from '../components/AppModal';
import { SkeletonList } from '../components/ShimmerLoading';
import ApiService from '../services/ApiService';
import { Customer, CustomerType } from '../models/types';
import { formatCurrency } from '../utils/helpers';

const CustomerListScreen = ({ navigation }: any) => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [customerBalances, setCustomerBalances] = useState<{ [key: string]: { balance: number; advance: number } }>({});
    const [selectedType, setSelectedType] = useState<CustomerType>('sale');
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const loadCustomers = async (showLoader = true) => {
        if (showLoader && isInitialLoad) {
            setLoading(true);
        }
        try {
            const data = await ApiService.getCustomers(selectedType);
            setCustomers(data);

            // Load balances for each customer
            const balances: { [key: string]: { balance: number; advance: number } } = {};
            for (const customer of data) {
                const balance = await ApiService.getCustomerBalance(customer.id);
                balances[customer.id] = { balance: balance.balance, advance: balance.advance };
            }
            setCustomerBalances(balances);
        } catch (error) {
            console.error('Error loading customers:', error);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        await loadCustomers(false);
        setRefreshing(false);
    };

    useFocusEffect(
        useCallback(() => {
            loadCustomers();
        }, [selectedType])
    );

    const handleDeleteCustomer = (customer: Customer) => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Customer',
            message: `Are you sure you want to delete "${customer.name}"?`,
            onConfirm: async () => {
                setDeleteLoading(true);
                try {
                    await ApiService.deleteCustomer(customer.id);
                    setModal(prev => ({ ...prev, visible: false }));
                    loadCustomers();
                } catch (error) {
                    console.error('Error deleting customer:', error);
                    setModal({
                        visible: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to delete customer.',
                    });
                } finally {
                    setDeleteLoading(false);
                }
            },
        });
    };

    const renderCustomerItem = ({ item }: { item: Customer }) => {
        return (
            <TouchableOpacity
                style={styles.customerCard}
                onPress={() => navigation.navigate('CustomerDetail', { customer: item })}
                activeOpacity={0.7}
            >
                <View style={styles.cardHeader}>
                    <View style={styles.customerInfo}>
                        <Icon
                            name={selectedType === 'sale' ? 'account' : 'account-tie'}
                            size={40}
                            color={theme.colors.primary}
                            style={styles.avatar}
                        />
                        <View style={styles.customerDetails}>
                            <Text style={styles.customerName}>{item.name}</Text>
                            {item.phone && (
                                <Text style={styles.customerPhone}>
                                    <Icon name="phone" size={12} color={theme.colors.textSecondary} /> {item.phone}
                                </Text>
                            )}
                        </View>
                    </View>
                    <Icon name="chevron-right" size={24} color={theme.colors.textLight} />
                </View>
            </TouchableOpacity>
        );
    };

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <Icon name="account-group-outline" size={64} color={theme.colors.textLight} />
            <Text style={styles.emptyTitle}>No Customers Found</Text>
            <Text style={styles.emptyText}>
                Add {selectedType === 'sale' ? 'buyers' : 'suppliers'} to get started
            </Text>
        </View>
    );

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader title="Customers" />

            {/* Type Toggle */}
            <View style={styles.typeToggle}>
                <TouchableOpacity
                    style={[styles.toggleButton, selectedType === 'sale' && styles.toggleButtonActive]}
                    onPress={() => setSelectedType('sale')}
                >
                    <Icon name="account" size={18} color={selectedType === 'sale' ? '#fff' : theme.colors.text} />
                    <Text style={[styles.toggleText, selectedType === 'sale' && styles.toggleTextActive]}>
                        Buyers {selectedType === 'sale' ? `(${customers.length})` : ''}
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.toggleButton, selectedType === 'purchase' && styles.toggleButtonActive]}
                    onPress={() => setSelectedType('purchase')}
                >
                    <Icon name="account-tie" size={18} color={selectedType === 'purchase' ? '#fff' : theme.colors.text} />
                    <Text style={[styles.toggleText, selectedType === 'purchase' && styles.toggleTextActive]}>
                        Suppliers {selectedType === 'purchase' ? `(${customers.length})` : ''}
                    </Text>
                </TouchableOpacity>
            </View>

            {/* Customer List */}
            {loading && isInitialLoad ? (
                <View style={styles.skeletonWrapper}>
                    <SkeletonList itemCount={6} />
                </View>
            ) : (
                <FlashList
                    data={customers}
                    keyExtractor={(item) => item.id}
                    renderItem={renderCustomerItem}
                    contentContainerStyle={styles.listContent}
                    ListEmptyComponent={renderEmpty}
                    refreshing={refreshing}
                    onRefresh={onRefresh}
                />
            )}

            {/* FAB */}
            <TouchableOpacity
                style={styles.fab}
                onPress={() => navigation.navigate('AddEditCustomer', { customerType: selectedType })}
            >
                <Icon name="plus" size={28} color="#fff" />
            </TouchableOpacity>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
                loading={deleteLoading}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        // paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.primary,
    },
    menuButton: {
        padding: 8,
    },
    headerTitle: {
        ...theme.typography.h5,
        color: '#fff',
        textAlign: 'center',
        flex: 1,
    },
    typeToggle: {
        flexDirection: 'row',
        margin: theme.spacing.md,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.xs,
    },
    toggleButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        gap: 6,
    },
    toggleButtonActive: {
        backgroundColor: theme.colors.primary,
    },
    toggleText: {
        ...theme.typography.body2,
        fontWeight: '600',
        color: theme.colors.text,
    },
    toggleTextActive: {
        color: '#fff',
    },
    listContent: {
        paddingHorizontal: theme.spacing.md,
        paddingBottom: 100,
    },
    customerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    customerInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    avatar: {
        marginRight: theme.spacing.sm,
    },
    customerDetails: {
        flex: 1,
    },
    customerName: {
        ...theme.typography.h6,
        color: theme.colors.text,
    },
    customerPhone: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    actionIcons: {
        flexDirection: 'row',
    },
    iconButton: {
        padding: 8,
    },
    balanceSection: {
        flexDirection: 'row',
        marginTop: theme.spacing.sm,
        gap: theme.spacing.sm,
    },
    balanceBadge: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.sm,
    },
    pendingBadge: {
        backgroundColor: '#FFF3E0',
    },
    advanceBadge: {
        backgroundColor: '#E8F5E9',
    },
    balanceLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    balanceAmount: {
        ...theme.typography.body2,
        fontWeight: '700',
        color: theme.colors.warning,
    },
    advanceAmount: {
        color: theme.colors.success,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 100,
    },
    emptyTitle: {
        ...theme.typography.h5,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    emptyText: {
        ...theme.typography.body2,
        color: theme.colors.textLight,
        marginTop: theme.spacing.xs,
    },
    fab: {
        position: 'absolute',
        right: theme.spacing.lg,
        bottom: theme.spacing.lg,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        ...theme.shadows.md,
    },
    skeletonWrapper: {
        flex: 1,
        paddingHorizontal: theme.spacing.md,
        paddingTop: theme.spacing.md,
    },
});

export default CustomerListScreen;
